import React from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
} from "@mui/material";
import { colors } from "../../../../theme";

/**
 * CommonTable Component
 * Centralized, reusable data table.
 *
 * @param {Object} props
 * @param {Array<{id: string, label: React.ReactNode, align?: string, width?: string|number, minWidth?: string|number, render?: Function, headerSx?: Object, cellSx?: Object}>} props.columns
 * @param {Array<Object>} props.rows
 * @param {string|Function} [props.rowKey="id"]
 * @param {boolean} [props.loading=false]
 * @param {React.ReactNode} [props.emptyMessage="No data available"]
 * @param {number|string} [props.minWidth=680]
 * @param {string} [props.size="medium"]
 * @param {boolean} [props.hover=true]
 * @param {Function} [props.onRowClick]
 * @param {Object} [props.containerSx]
 * @param {Object} [props.tableSx]
 * @param {Object} [props.headRowSx]
 * @param {Object} [props.bodyRowSx]
 */
const CommonTable = ({
  columns = [],
  rows = [],
  rowKey = "id",
  loading = false,
  emptyMessage = "No data available",
  minWidth = 680,
  size = "medium",
  hover = true,
  onRowClick,
  containerSx = {},
  tableSx = {},
  headRowSx = {},
  bodyRowSx = {},
  ...props
}) => {
  const getRowKey = (row, index) => {
    if (typeof rowKey === "function") {
      return rowKey(row, index);
    }
    return row[rowKey] || row.id || row.key || `row-${index}`;
  };

  return (
    <TableContainer
      sx={{
        overflowX: "auto",
        ...containerSx,
      }}
    >
      <Table
        size={size}
        sx={{
          minWidth: minWidth,
          ...tableSx,
        }}
        {...props}
      >
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: colors.dashboard.tableHeader,
              ...headRowSx,
            }}
          >
            {columns.map((col) => (
              <TableCell
                key={col.id}
                align={col.align || "left"}
                sx={{
                  color: colors.slate[600],
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  py: 1.25,
                  px: 2,
                  borderBottom: `1px solid ${colors.dashboard.cardBorder}`,
                  whiteSpace: "nowrap",
                  ...(col.width ? { width: col.width } : {}),
                  ...(col.minWidth ? { minWidth: col.minWidth } : {}),
                  ...(col.headerSx || {}),
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            Array.from({ length: 4 }).map((_, rIdx) => (
              <TableRow key={`skeleton-${rIdx}`}>
                {columns.map((col, cIdx) => (
                  <TableCell
                    key={`skeleton-c-${cIdx}`}
                    align={col.align || "left"}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: `1px solid ${colors.dashboard.tableBorder}`,
                    }}
                  >
                    <Skeleton variant="text" width="80%" height={24} />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : rows && rows.length > 0 ? (
            rows.map((row, index) => (
              <TableRow
                key={getRowKey(row, index)}
                hover={hover}
                onClick={onRowClick ? (e) => onRowClick(row, index, e) : undefined}
                sx={{
                  cursor: onRowClick ? "pointer" : "default",
                  "&:hover": hover
                    ? { backgroundColor: colors.dashboard.tableRowHover }
                    : undefined,
                  transition: "background-color 0.15s ease-in-out",
                  ...bodyRowSx,
                }}
              >
                {columns.map((col) => {
                  const cellValue = row ? row[col.id] : undefined;
                  let renderedContent = cellValue;

                  if (typeof col.render === "function") {
                    try {
                      renderedContent = col.render(cellValue, row, index);
                    } catch (renderErr) {
                      try {
                        renderedContent = col.render(row, row, index);
                      } catch {
                        renderedContent = cellValue;
                      }
                    }
                  }

                  return (
                    <TableCell
                      key={col.id}
                      align={col.align || "left"}
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderBottom: `1px solid ${colors.dashboard.tableBorder}`,
                        fontSize: "0.84rem",
                        color: colors.slate[900],
                        ...(col.cellSx || {}),
                      }}
                    >
                      {renderedContent !== undefined && renderedContent !== null
                        ? renderedContent
                        : "—"}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                align="center"
                sx={{
                  py: 4,
                  color: colors.slate[500],
                  borderBottom: `1px solid ${colors.dashboard.tableBorder}`,
                }}
              >
                <Typography variant="body2" sx={{ color: colors.slate[500] }}>
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

CommonTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      align: PropTypes.oneOf(["left", "center", "right", "justify"]),
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      render: PropTypes.func,
      headerSx: PropTypes.object,
      cellSx: PropTypes.object,
    })
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  loading: PropTypes.bool,
  emptyMessage: PropTypes.node,
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  size: PropTypes.oneOf(["small", "medium"]),
  hover: PropTypes.bool,
  onRowClick: PropTypes.func,
  containerSx: PropTypes.object,
  tableSx: PropTypes.object,
  headRowSx: PropTypes.object,
  bodyRowSx: PropTypes.object,
};

export default CommonTable;
